import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { navigationItems } from '../../navigation/navigations';
import AuthenticatedFileImage from '../../components/AuthenticatedFileImage';
import localStorageService from 'services/localStorageService';


// Icon mapping for better performance and reliability
/* const iconMap = {
  Person: require('@mui/icons-material/Person').default,
};
 */
// Dynamic icon import function
/* const getIconComponent = (iconName) => {
  return iconMap[iconName] || iconMap.Person;
}; */

const findParentMenus = (items, path, parents = []) => {
  for (const item of items) {
    if (item.path === path) return parents;
    if (item.submenu) {
      const found = findParentMenus(item.submenu, path, [...parents, item.title]);
      if (found) return found;
    }
  }
  return null;
};

const Sidebar = ({ open, onClose, drawerWidth = 240, collapsedWidth = 64, collapsed, setCollapsed, hovered, setHovered }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const authState = useSelector(state => state.auth);
  const [expandedItems, setExpandedItems] = useState({});
  const isSidebarExpanded = isMobile ? true : (!collapsed || hovered);
  const hoverTimeout = useRef();
  const activeAuthUser = user || {};
  const fullName = activeAuthUser?.full_name || activeAuthUser?.name || 'User';
  const userRole = authState.selectedRole || activeAuthUser?.roles?.[0] || activeAuthUser?.user?.designation?.name || activeAuthUser?.user?.roles?.[0]?.name || 'User';
  const profilePicName = activeAuthUser?.user?.profile_pic_name;
  const profilePicPath = activeAuthUser?.user?.profile_pic_path;

  const getInitials = () => {
    const names = fullName.split(' ');
    return names.map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    localStorageService.removeItem('all_user_roles');
    localStorageService.removeItem('selected_user_role');
    navigate('/login');
  };

  // Auto-expand parent menus based on current route
  useEffect(() => {
    const parents = findParentMenus(navigationItems, location.pathname);
    if (parents) {
      setExpandedItems((prev) => {
        const newExpanded = { ...prev };
        parents.forEach((title) => {
          newExpanded[title] = true;
        });
        return newExpanded;
      });
    }
  }, [location.pathname]);




  const handleMouseEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setHovered(true);
  };
  const handleMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHovered(false);
    }, 150);
  };

  const handleItemClick = (item) => {
    if (item.submenu) {
      setExpandedItems(prev => ({
        ...prev,
        [item.title]: !prev[item.title]
      }));
    } else if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onClose();
      }
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const normalize = (r) => r.trim().replace(/\s+/g, ' ').toLowerCase();
  const hasAnyRole = (roles) => {
    if (!user || !user.roles) return false;
    const userRoles = user.roles.map(normalize);
    return roles.some(role => userRoles.includes(normalize(role)));
  };

  const renderMenuItem = (item, level = 0) => {
    if(!item.roles && item.submenu){
      item.roles=[...new Set(item.submenu.flatMap(item => item.roles))]
    }
    if (item.roles && !hasAnyRole(item.roles)) return null;
    const iconProp = item.icon;
    let iconElement;
    if (React.isValidElement(iconProp)) {
      iconElement = iconProp;
    } else {
      //const IconComponent = getIconComponent(iconProp);
      //iconElement = <IconComponent />;
    }
    const isExpanded = expandedItems[item.title];
    const isItemActive = item.path ? isActive(item.path) : false;
    return (
      <Box key={item.title}>
        <ListItem style={{ width: 'auto' }} disablePadding sx={{
          borderRadius: 2,
          mb: 0.5,
          //mx: 1,
        }}>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            className={isItemActive ? 'SidebarMenuItem-active' : ''}
            sx={{
              pl: isSidebarExpanded ? (level * 1 + 3) : 2,
              py: 0.7,
              borderRadius: 2,
              backgroundColor: isItemActive ? theme.palette.sidebar.menuItem.backgroundActive : theme.palette.sidebar.menuItem.background,
              color: isItemActive ? theme.palette.sidebar.menuItem.textActive : theme.palette.sidebar.menuItem.text,
              fontWeight: isItemActive ? 600 : 400,
              '&:hover': {
                backgroundColor: isItemActive ? theme.palette.sidebar.menuItem.backgroundActive : theme.palette.sidebar.menuItem.backgroundHover,
              },
              transition: 'background 0.2s',
              justifyContent: isSidebarExpanded ? 'flex-start' : 'center',
            }}
          >
            <ListItemIcon sx={{
              color: isItemActive ? theme.palette.sidebar.menuItem.iconActive : theme.palette.sidebar.menuItem.icon,
              minWidth: 0,
              mr: isSidebarExpanded ? 1 : 0,
              justifyContent: 'center',
              borderRadius: '8px',
              //background: isItemActive ? 'linear-gradient(135deg, #f5f6fa 0%, #e9ecef 100%)' : 'none',
              boxShadow: isItemActive ? '0 2px 8px 0 rgba(34, 41, 47, 0.04)' : 'none',
              //p: isItemActive ? 0.5 : 0,
            }}>
              {iconElement}
            </ListItemIcon>
            <Box
              sx={{
                overflow: 'hidden',
                transition: 'opacity 0.3s, width 0.3s',
                opacity: isSidebarExpanded ? 1 : 0,
                width: isSidebarExpanded ? 'auto' : 0,
                pointerEvents: isSidebarExpanded ? 'auto' : 'none',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontWeight: isItemActive ? 700 : 500,
                  fontSize: '0.875rem',
                }}
              />
              {item.submenu && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
            </Box>
          </ListItemButton>
        </ListItem>
        {item.submenu && isSidebarExpanded && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.submenu.map((subItem) => renderMenuItem(subItem, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  // App info card at the top
  const appInfoCard = isSidebarExpanded ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 2,
        px: 1,
        py: 0.6,
        mx: 1,
        my: 1.2,
        boxShadow: 0,
        transition: 'opacity 0.3s',
        opacity: 1,
        pointerEvents: 'auto',
      }}
    >
      <Box
        sx={{
          //bgcolor: theme.palette.grey[100],
          borderRadius: 2,
          width: 38,
          height: 38,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img
          src="/assets/siteLogo/logo.png"
          alt="Site Logo"
          style={{ width: 38, height: 38, objectFit: 'contain' }}
        />
      </Box>
      <Box
        sx={{
          transition: 'opacity 0.3s, width 0.3s',
          opacity: 1,
          width: 'auto',
          overflow: 'hidden',
          pointerEvents: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        <Box sx={{ fontWeight: 700, fontSize: 16, color: theme.palette.sidebar.logoTextColor }}>
          NCEP
        </Box>
        <Box sx={{ fontSize: 10, color: theme.palette.sidebar.logoSloganColor }}>
          National Codex Engagement Platform
        </Box>
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: 64,
        my: 1.2,
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
          borderRadius: 2,
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img
          src="/assets/siteLogo/logo.png"
          alt="Site Logo"
          style={{ width: 32, height: 32, objectFit: 'contain' }}
        />
      </Box>
    </Box>
  );



  const drawerContent = (
    <Box
      sx={{
        bgcolor: theme.palette.sidebar.background,
        height: '100vh',
        pt: 0,
        transition: 'width 0.3s',
        overflowX: 'hidden',
        //zoom: 0.9,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      onMouseEnter={!isMobile && collapsed ? handleMouseEnter : undefined}
      onMouseLeave={!isMobile && collapsed ? handleMouseLeave : undefined}
    >
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {appInfoCard}
        <div className='px-4 w-full'>
          <Divider style={{borderColor:'#5b5b5b'}}/>
        </div>

        {/*  {!isMobile && toggleButton} */}
        <List sx={{ mt: 1, pb: 2 }}>
          {navigationItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Footer with User Info and Logout */}
      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
          p: isSidebarExpanded ? 2 : 1,
          bgcolor: theme.palette.sidebar.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSidebarExpanded ? 'flex-start' : 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {isSidebarExpanded && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              {profilePicName && profilePicPath ? (
                <AuthenticatedFileImage fileName={profilePicName} filePath={profilePicPath}>
                  {({ imageSrc }) => (
                    <Avatar src={imageSrc || undefined} sx={{ width: 40, height: 40, flexShrink: 0 }}>
                      {!imageSrc && getInitials()}
                    </Avatar>
                  )}
                </AuthenticatedFileImage>
              ) : (
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: '#4CAF50',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {getInitials()}
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box 
                  sx={{ 
                    fontSize: 13, 
                    fontWeight: 600, 
                    color: theme.palette.sidebar.menuItem.text, 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis' 
                  }}
                >
                  {fullName}
                </Box>
                <Box 
                  sx={{ 
                    fontSize: 11, 
                    color: theme.palette.sidebar.menuItem.text, 
                    opacity: 0.6, 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis' 
                  }}
                >
                  {userRole}
                </Box>
              </Box>
            </Box>
            <Box
              onClick={handleLogout}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                color: theme.palette.sidebar.menuItem.text,
                fontSize: 13,
                fontWeight: 500,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              <span>Logout</span>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: isSidebarExpanded ? drawerWidth : collapsedWidth,
          transition: 'width 0.2s',
          overflowX: 'hidden',
        },
      }}
      open
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
